import type { Permission, Role } from '@/types/auth';

export interface AuthDataSources {
  /** Permissions from session data (useSettings userSessionData) — single value or array */
  sessionPermissions?: Permission[] | Permission;
  /** Roles from session data (useSettings userSessionData) — single value or array */
  sessionRoles?: Role[] | Role;
  /** Whether settings reports the user as authenticated (has is_authenticated + id) */
  settingsAuthenticated: boolean;
  /** Permissions from the JWT token (getAuthorizationToken API) — single value or array */
  tokenPermissions?: Permission[] | Permission;
  /** Roles from the JWT token (getAuthorizationToken API) — single value or array */
  tokenRoles?: Role[] | Role;
}

export interface ResolvedAuthState {
  permissions: Permission[];
  roles: Role[];
  source: 'unauthenticated' | 'token' | 'session' | 'default';
}

/**
 * Pure function that resolves the user's auth state from multiple data sources.
 *
 * Priority chain:
 * 1. If settings says user is NOT authenticated → clear all auth (prevents limbo state)
 * 2. If valid JWT token has permissions/roles → use token data
 * 3. Fall back to session data from useSettings
 * 4. If a source has roles but no permissions field → use its roles with empty
 *    permissions (the backend omits the field when the user has none; without
 *    this the user loses the 'User' role and the UI treats them as logged out)
 * 5. Default to unauthenticated (Free role, empty permissions)
 */
function toArray<T>(value: T | T[]): T[] {
  return Array.isArray(value) ? value : [value];
}

export function resolveAuthState(sources: AuthDataSources): ResolvedAuthState {
  // Priority 1: Settings says not authenticated — clear everything to prevent limbo
  if (!sources.settingsAuthenticated) {
    return {
      permissions: [],
      roles: ['Free'],
      source: 'unauthenticated',
    };
  }

  // Priority 2: Use token data if available (explicit undefined check — empty arrays are valid)
  if (
    sources.tokenPermissions !== undefined &&
    sources.tokenRoles !== undefined
  ) {
    return {
      permissions: toArray(sources.tokenPermissions ?? []),
      roles: toArray(sources.tokenRoles),
      source: 'token',
    };
  }

  // Priority 3: Fall back to session data (explicit undefined check — empty arrays are valid)
  if (
    sources.sessionPermissions !== undefined &&
    sources.sessionRoles !== undefined
  ) {
    return {
      permissions: toArray(sources.sessionPermissions ?? []),
      roles: toArray(sources.sessionRoles),
      source: 'session',
    };
  }

  // Priority 4: Roles-only sources — a missing permissions field means the
  // user has none, not that the source is invalid
  //pausing a subscription means the user has no permissions
  if (sources.tokenRoles !== undefined) {
    return {
      permissions: [],
      roles: toArray(sources.tokenRoles),
      source: 'token',
    };
  }

  // Priority 5: No data available — default to unauthenticated
  return {
    permissions: [],
    roles: ['Free'],
    source: 'default',
  };
}
